import styled from 'styled-components';

const PanoramaCard = ({ category, title, description, image, href, status, placeholder, actionLabel, isPending }) => {
  const linkProps = href
    ? { as: 'a', href, target: '_blank', rel: 'noreferrer' }
    : { as: 'article' };

  return (
    <Card $interactive={Boolean(href)} $pending={isPending} {...linkProps}>
      <Media>
        {image ? (
          <Thumbnail src={image} alt={`${title} 미리보기`} />
        ) : (
          <Placeholder $variant={placeholder?.variant} $pending={isPending} aria-hidden="true">
            <Grid />
            {placeholder?.variant === 'goheung' && <TerrainLines />}
            <PlaceholderContent $variant={placeholder?.variant}>
              {placeholder?.logo ? (
                <PlaceholderLogoFrame $variant={placeholder?.variant} $pending={isPending}>
                  <PlaceholderLogo src={placeholder.logo} alt="" />
                </PlaceholderLogoFrame>
              ) : (
                <PlaceholderText>{placeholder?.title || '360°'}</PlaceholderText>
              )}
              {placeholder?.label && <PlaceholderLabel $variant={placeholder?.variant}>{placeholder.label}</PlaceholderLabel>}
            </PlaceholderContent>
          </Placeholder>
        )}
        <Category $pending={isPending}>{category}</Category>
        {status && <Status>{status}</Status>}
      </Media>
      <Content $pending={isPending}>
        <Title>{title}</Title>
        <Description>{description}</Description>
        <Action>{href ? '프로젝트 보기 ↗' : (actionLabel || '썸네일 준비 중')}</Action>
      </Content>
    </Card>
  );
};

export default PanoramaCard;

const Card = styled.article`
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(33, 47, 57, 0.1);
  border-radius: 20px;
  background: #ffffff;
  color: inherit;
  text-decoration: none;
  box-shadow: 0 16px 36px rgba(24, 38, 46, 0.08);
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;

  ${({ $pending }) =>
    $pending && `
      border-color: rgba(33, 47, 57, 0.07);
      box-shadow: 0 10px 26px rgba(24, 38, 46, 0.055);
    `}

  ${({ $interactive }) =>
    $interactive &&
    `
      cursor: pointer;

      &:hover,
      &:focus-visible {
        transform: translateY(-6px);
        border-color: rgba(74, 154, 188, 0.55);
        box-shadow: 0 24px 50px rgba(24, 38, 46, 0.16);
        outline: none;
      }
    `}
`;

const Media = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #dfe8ea;
`;

const Thumbnail = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 300ms ease;

  ${Card}:hover & {
    transform: scale(1.045);
  }
`;

const Placeholder = styled.div`
  position: relative;
  display: grid;
  width: 100%;
  height: 100%;
  place-items: center;
  overflow: hidden;
  background:
    radial-gradient(circle at 24% 18%, rgba(120, 211, 230, 0.94), transparent 29%),
    radial-gradient(circle at 82% 72%, rgba(50, 105, 131, 0.84), transparent 35%),
    linear-gradient(135deg, #102e3a, #24566b 55%, #6ba7b5);

  ${({ $variant }) =>
    $variant === 'goheung' && `
      background:
        radial-gradient(circle at 20% 18%, rgba(87, 189, 201, 0.42), transparent 28%),
        radial-gradient(circle at 82% 76%, rgba(36, 80, 98, 0.72), transparent 36%),
        linear-gradient(135deg, #0d2934 0%, #174757 56%, #276b79 100%);
    `}

  ${({ $pending }) =>
    $pending && `
      &::after {
        position: absolute;
        inset: 0;
        z-index: 0;
        content: '';
        background: rgba(8, 39, 50, 0.4);
      }
    `}
`;

const Grid = styled.div`
  position: absolute;
  inset: -60%;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.14) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.14) 1px, transparent 1px);
  background-size: 28px 28px;
  transform: rotate(-18deg);
`;

const TerrainLines = styled.div`
  position: absolute;
  inset: -44%;
  opacity: 0.78;
  background:
    repeating-radial-gradient(ellipse at 37% 56%, transparent 0 27px, rgba(206, 245, 242, 0.16) 28px 30px, transparent 31px 50px),
    repeating-radial-gradient(ellipse at 68% 34%, transparent 0 34px, rgba(206, 245, 242, 0.09) 35px 37px, transparent 38px 61px);
  transform: rotate(-13deg) scale(1.12);
`;

const PlaceholderContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 11px;

  ${({ $variant }) =>
    $variant === 'goheung' && `
      display: grid;
      place-items: center;

      & > * {
        grid-area: 1 / 1;
      }
    `}
`;

const PlaceholderText = styled.span`
  color: rgba(255, 255, 255, 0.92);
  font-size: clamp(2rem, 4.3vw, 3.35rem);
  font-family: var(--font-display);
  font-weight: var(--font-weight-title);
  letter-spacing: 0.025em;
  line-height: 1;
`;

const PlaceholderLogoFrame = styled.div`
  width: min(255px, 64vw);

  ${({ $variant }) =>
    $variant === 'goheung' && `
      width: min(205px, 52vw);
    `}

  ${({ $pending }) =>
    $pending && `
      opacity: 0.3;
      filter: blur(0.7px);
    `}
`;

const PlaceholderLogo = styled.img`
  display: block;
  width: 100%;
  height: auto;
  filter: brightness(0) invert(1) drop-shadow(0 5px 16px rgba(0, 18, 25, 0.36));
`;

const PlaceholderLabel = styled.span`
  position: relative;
  z-index: 2;
  padding: 7px 10px;
  border: 1px solid rgba(217, 249, 245, 0.42);
  border-radius: 999px;
  background: rgba(11, 38, 48, 0.25);
  color: #d9f9f5;
  font-size: 0.64rem;
  font-weight: 850;
  letter-spacing: 0.16em;
  line-height: 1;

  ${({ $variant }) =>
    $variant === 'goheung' && `
      border-color: rgba(226, 255, 251, 0.72);
      background: rgba(8, 35, 44, 0.72);
      color: #f1fffd;
      box-shadow: 0 6px 18px rgba(0, 20, 27, 0.3);
    `}
`;

const Category = styled.span`
  position: absolute;
  top: 16px;
  left: 16px;
  padding: 7px 10px;
  border-radius: 999px;
  background: rgba(10, 30, 38, 0.75);
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 850;
  letter-spacing: 0.09em;
  line-height: 1.2;
  opacity: ${({ $pending }) => ($pending ? 0.58 : 1)};
`;

const Status = styled.span`
  position: absolute;
  right: 16px;
  bottom: 16px;
  padding: 7px 10px;
  border-radius: 999px;
  background: #e8f5e4;
  color: #29653a;
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const Content = styled.div`
  display: flex;
  min-height: 154px;
  flex: 1;
  flex-direction: column;
  padding: 22px;
  opacity: ${({ $pending }) => ($pending ? 0.58 : 1)};
  transition: opacity 180ms ease;
`;

const Title = styled.h3`
  margin: 0;
  color: #20323a;
  font-size: clamp(1.18rem, 1.45vw, 1.34rem);
  font-weight: var(--font-weight-title);
  letter-spacing: -0.055em;
  line-height: 1.25;
`;

const Description = styled.p`
  margin: 10px 0 0;
  color: #68777d;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.62;
  word-break: keep-all;
`;

const Action = styled.span`
  margin-top: auto;
  padding-top: 18px;
  color: #2d7089;
  font-size: 0.85rem;
  font-weight: 850;
  letter-spacing: -0.025em;
`;
